<template>
	<v-container class="pa-2 pa-sm-3" max-width="900">
		<v-alert v-if="store.error" type="error" class="mb-3" variant="tonal" density="compact">{{ store.error }}</v-alert>

		<v-card class="mb-3">
			<v-card-title>Display names</v-card-title>
			<v-card-text v-if="store.dirty">
				<v-row dense>
					<v-col cols="12" sm="4">
						<v-text-field v-model="leftName" label="Left side" hint="e.g. Stephen's Side" persistent-hint density="compact" />
					</v-col>
					<v-col cols="12" sm="4">
						<v-text-field v-model="rightName" label="Right side" hint="e.g. Laura's Side" persistent-hint density="compact" />
					</v-col>
					<v-col cols="12" sm="4">
						<v-text-field v-model="roomNameField" label="Room" hint="e.g. Bedroom" persistent-hint density="compact" />
					</v-col>
				</v-row>
			</v-card-text>
		</v-card>

		<v-card class="mb-3">
			<v-card-title>Tuning</v-card-title>
			<v-card-text v-if="store.dirty">
				<v-row dense>
					<v-col cols="6" sm="6">
						<v-text-field v-model.number="store.dirty.targetBedTemp" type="number" label="Fallback target (°F)" :hint="hint('target')" persistent-hint density="compact" />
					</v-col>
					<v-col cols="6" sm="6">
						<v-text-field v-model.number="store.dirty.roomNeutralTemp" type="number" label="Room neutral (°F)" :hint="hint('neutral')" persistent-hint density="compact" />
					</v-col>
					<v-col cols="6" sm="6">
						<v-text-field v-model.number="store.dirty.roomTempInfluence" type="number" step="0.1" label="Room influence" :hint="hint('influence')" persistent-hint density="compact" />
					</v-col>
					<v-col cols="6" sm="6">
						<v-text-field v-model.number="store.dirty.hysteresisF" type="number" step="0.1" label="Hysteresis (°F)" :hint="hint('hysteresis')" persistent-hint density="compact" />
					</v-col>
					<v-col cols="6" sm="6">
						<v-text-field v-model.number="store.dirty.manualHoldMinutes" type="number" label="Hold (min)" :hint="hint('hold')" persistent-hint density="compact" />
					</v-col>
					<v-col cols="6" sm="6">
						<v-text-field v-model.number="store.dirty.loopIntervalSeconds" type="number" label="Loop (sec)" :hint="hint('interval')" persistent-hint density="compact" />
					</v-col>
					<v-col cols="6" sm="6">
						<v-text-field v-model.number="store.dirty.preheatMinutes" type="number" label="Schedule start run-in (min)" :hint="hint('preheat')" persistent-hint density="compact" />
					</v-col>
					<v-col cols="6" sm="6">
						<v-text-field v-model.number="store.dirty.autoStopMinutesNoPresence" type="number" label="Out-of-bed auto stop (min)" :hint="hint('autoStop')" persistent-hint density="compact" />
					</v-col>
				</v-row>
			</v-card-text>
			<v-card-actions>
				<v-btn variant="text" :disabled="!isDirty" @click="reset">Reset</v-btn>
				<v-spacer />
				<v-btn color="primary" :loading="store.saving" :disabled="!isDirty" @click="save">Save</v-btn>
			</v-card-actions>
		</v-card>

		<v-card class="mb-3">
			<v-card-title>Per-side limits</v-card-title>
			<v-card-text v-if="store.dirty">
				<div class="text-caption text-medium-emphasis mb-2">
					Caps the room-adjusted setpoint per side. Leave blank for no cap. Doesn't affect manual overrides or the base target — only the room-adjustment correction.
				</div>
				<v-row dense>
					<v-col cols="6">
						<div class="text-subtitle-2 mb-2">{{ leftName || "Left" }}</div>
						<v-text-field v-model.number="leftCeiling" type="number" label="Ceiling (°F)" placeholder="—" hint="Setpoint won't go above this when room-adjusting" persistent-hint density="compact" clearable class="mb-2" />
						<v-text-field v-model.number="leftFloor" type="number" label="Floor (°F)" placeholder="—" hint="Setpoint won't go below this when room-adjusting" persistent-hint density="compact" clearable />
					</v-col>
					<v-col cols="6">
						<div class="text-subtitle-2 mb-2">{{ rightName || "Right" }}</div>
						<v-text-field v-model.number="rightCeiling" type="number" label="Ceiling (°F)" placeholder="—" hint="Setpoint won't go above this when room-adjusting" persistent-hint density="compact" clearable class="mb-2" />
						<v-text-field v-model.number="rightFloor" type="number" label="Floor (°F)" placeholder="—" hint="Setpoint won't go below this when room-adjusting" persistent-hint density="compact" clearable />
					</v-col>
				</v-row>
			</v-card-text>
			<v-card-actions>
				<v-btn variant="text" :disabled="!isDirty" @click="reset">Reset</v-btn>
				<v-spacer />
				<v-btn color="primary" :loading="store.saving" :disabled="!isDirty" @click="save">Save</v-btn>
			</v-card-actions>
		</v-card>

		<v-card class="mb-3">
			<v-card-title>Bed presence</v-card-title>
			<v-card-text>
				<div class="text-caption text-medium-emphasis mb-1">
					Auto-stop the dock when the side is empty and resume when occupied during the sleep schedule. Getting in bed during the day (outside the schedule) never starts the dock.
				</div>
				<v-switch v-model="presenceRequiredLeft" color="primary" :label="'Presence automation — ' + (leftName || 'Left')" hide-details density="compact" />
				<v-switch v-model="presenceRequiredRight" color="primary" :label="'Presence automation — ' + (rightName || 'Right')" hide-details density="compact" />
				<v-switch v-model="sleepiqSidesSwapped" color="primary" label="Swap SleepIQ sides" hint="Turn on if presence shows on the wrong side (SleepIQ reports left/right from the foot of the bed)." persistent-hint density="compact" class="mt-2" />
			</v-card-text>
		</v-card>

		<v-card class="mb-3">
			<v-card-title>Off window</v-card-title>
			<v-card-text v-if="store.dirty">
				<div class="text-caption text-medium-emphasis mb-1">
					Daily window when the bed stays off. At the start the dock goes to standby even if a side still reads occupied; during it, sitting on the bed or a schedule entry won't start it. Start dock or Override still work. Afterwards the dock waits for the next schedule entry.
				</div>
				<v-switch v-model="offWindowEnabled" color="primary" label="Enable off window" hide-details density="compact" />
				<v-row dense class="mt-1">
					<v-col cols="6">
						<v-text-field v-model="offWindowStart" type="time" label="Off from" :disabled="!offWindowEnabled" density="compact" hide-details />
					</v-col>
					<v-col cols="6">
						<v-text-field v-model="offWindowEnd" type="time" label="Until" :disabled="!offWindowEnabled" density="compact" hide-details />
					</v-col>
				</v-row>
			</v-card-text>
			<v-card-actions>
				<v-btn variant="text" :disabled="!isDirty" @click="reset">Reset</v-btn>
				<v-spacer />
				<v-btn color="primary" :loading="store.saving" :disabled="!isDirty" @click="save">Save</v-btn>
			</v-card-actions>
		</v-card>

		<v-card class="mb-3">
			<v-card-title>Presence dropout handling</v-card-title>
			<v-card-text v-if="store.dirty">
				<div class="text-caption text-medium-emphasis mb-3">
					SleepIQ sometimes loses a sleeper mid-night and reports the bed empty while they're still in it, which would auto-stop their side. These modes keep heat running through those dropouts.
				</div>
				<v-radio-group v-model="presenceMode" hide-details density="compact" class="mb-1">
					<v-radio value="latch">
						<template #label>
							<div>
								<div>Overnight latch <span class="text-caption text-medium-emphasis">(default)</span></div>
								<div class="text-caption text-medium-emphasis">Once detected after bedtime, stay "occupied" until the morning wake schedule entry — ignore mid-night dropouts. Needs a bedtime + wake schedule.</div>
							</div>
						</template>
					</v-radio>
					<v-radio value="pressure">
						<template #label>
							<div>
								<div>Pressure fallback <span class="text-caption text-medium-emphasis">(experimental)</span></div>
								<div class="text-caption text-medium-emphasis">Also treat raised chamber pressure as occupied when SleepIQ says empty. Tracks real exits. Calibrate the empty bed first, then tune the threshold.</div>
							</div>
						</template>
					</v-radio>
				</v-radio-group>

				<div v-if="presenceMode === 'pressure'" class="mt-3">
					<v-alert v-if="calibrateMsg" :type="calibrateType" variant="tonal" density="compact" class="mb-3">{{ calibrateMsg }}</v-alert>
					<v-row dense align="center">
						<v-col cols="12" sm="5">
							<v-text-field v-model.number="presenceThreshold" type="number" label="Pressure threshold" hint="Units above the empty-bed baseline that count as occupied" persistent-hint density="compact" />
						</v-col>
						<v-col cols="12" sm="7">
							<div class="text-caption text-medium-emphasis">Empty-bed baseline</div>
							<div class="text-body-2">{{ leftName || "Left" }}: {{ baselineText("left") }} · {{ rightName || "Right" }}: {{ baselineText("right") }}</div>
						</v-col>
					</v-row>
					<div class="text-caption text-medium-emphasis mt-2 mb-1">Run this with <strong>nobody in bed</strong> — it records the current pressure as the "empty" reference.</div>
					<v-btn size="small" variant="tonal" :loading="calibrating" @click="calibrate">Calibrate empty bed</v-btn>
				</div>
			</v-card-text>
			<v-card-actions v-if="presenceMode === 'pressure'">
				<v-btn variant="text" :disabled="!isDirty" @click="reset">Reset</v-btn>
				<v-spacer />
				<v-btn color="primary" :loading="store.saving" :disabled="!isDirty" @click="save">Save threshold</v-btn>
			</v-card-actions>
		</v-card>

		<v-card>
			<v-card-title>Sign in</v-card-title>
			<v-card-text>
				<div class="text-body-2 mb-2">
					{{ hasToken
						? "This device holds the API token."
						: "Signed in with your richworks password, the same one as Stephen's other apps." }}
				</div>
				<v-btn variant="text" color="warning" :loading="signingOut" @click="signOut">Sign out</v-btn>
			</v-card-text>
		</v-card>
	</v-container>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import configStore from "../stores/configStore.js";
import statusStore from "../stores/statusStore.js";
import { api } from "../api.js";

var store = configStore.data;
var router = useRouter();
var hasToken = ref(!!api.getToken());

onMounted(() => configStore.methods.load());

// Presence-required is an auto-save toggle (like room-adjust on SideCard) so
// users don't wonder whether the form-level Save button applies to it. Reads
// from the live status store, writes through the same API the form uses.
// Per-side since 2026-07; a legacy boolean value applies to both sides.
function makePresenceRequired(side) {
	return computed({
		get: () => {
			var raw = statusStore.data.data?.config?.presenceRequiredToRun ?? store.dirty?.presenceRequiredToRun;
			if (typeof raw === "boolean") { return raw; }
			return raw?.[side] === true;
		},
		set: (v) => statusStore.methods.setPresenceRequired(side, v),
	});
}
var presenceRequiredLeft = makePresenceRequired("left");
var presenceRequiredRight = makePresenceRequired("right");

var sleepiqSidesSwapped = computed({
	get: () => store.dirty?.sleepiqSidesSwapped ?? false,
	set: (v) => {
		if (!store.dirty) { return; }
		store.dirty.sleepiqSidesSwapped = !!v;
		// Auto-save (matches the behavior of the other toggles above).
		configStore.methods.save();
	},
});

// Presence-mode is an auto-save toggle (like the swap above); the threshold uses
// the card's Save button so a half-typed number doesn't get persisted per keystroke.
var presenceMode = computed({
	get: () => store.dirty?.presenceMode ?? "latch",
	set: (v) => {
		if (!store.dirty) { return; }
		store.dirty.presenceMode = v;
		configStore.methods.save();
	},
});
var presenceThreshold = computed({
	get: () => store.dirty?.presencePressureThreshold ?? 100,
	set: (v) => { if (store.dirty) { store.dirty.presencePressureThreshold = v === "" || v == null ? null : Number(v); } },
});
function baselineText(side) {
	var v = store.dirty?.presenceBaseline?.[side];
	return v == null ? "not calibrated" : String(v);
}

var calibrating = ref(false);
var calibrateMsg = ref("");
var calibrateType = ref("success");
async function calibrate() {
	calibrating.value = true;
	calibrateMsg.value = "";
	try {
		var res = await statusStore.methods.calibratePresence();
		await configStore.methods.load(); // refresh displayed baselines
		var cap = res?.captured ?? {};
		var parts = ["left", "right"].map((s) => (leftName.value && s === "left" ? leftName.value : rightName.value && s === "right" ? rightName.value : s) + "=" + (cap[s] == null ? "—" : cap[s]));
		calibrateType.value = (cap.left == null && cap.right == null) ? "warning" : "success";
		calibrateMsg.value = (calibrateType.value === "warning" ? "No pressure returned (is SleepIQ live?). " : "Captured empty-bed baseline — ") + parts.join(", ");
	} catch (e) {
		calibrateType.value = "error";
		calibrateMsg.value = e.message;
	} finally {
		calibrating.value = false;
	}
}

// Off window: the times use the card's Save button; the switch auto-saves like
// the other toggles on this page.
function offWindowValue() {
	return { enabled: true, start: "08:00", end: "19:00", ...(store.dirty?.offWindow ?? {}) };
}
var offWindowEnabled = computed({
	get: () => store.dirty?.offWindow?.enabled ?? true,
	set: (v) => {
		if (!store.dirty) { return; }
		store.dirty.offWindow = { ...offWindowValue(), enabled: !!v };
		configStore.methods.save();
	},
});
function makeOffWindowTime(key) {
	return computed({
		get: () => offWindowValue()[key],
		set: (v) => { if (store.dirty && v) { store.dirty.offWindow = { ...offWindowValue(), [key]: v }; } },
	});
}
var offWindowStart = makeOffWindowTime("start");
var offWindowEnd = makeOffWindowTime("end");

var isDirty = computed(() => {
	if (!store.data || !store.dirty) { return false; }
	return JSON.stringify(store.data) !== JSON.stringify(store.dirty);
});

var leftName = computed({
	get: () => store.dirty?.sideNames?.left ?? "",
	set: (v) => {
		if (!store.dirty.sideNames) { store.dirty.sideNames = { left: "Left", right: "Right" }; }
		store.dirty.sideNames = { ...store.dirty.sideNames, left: v };
	},
});
var rightName = computed({
	get: () => store.dirty?.sideNames?.right ?? "",
	set: (v) => {
		if (!store.dirty.sideNames) { store.dirty.sideNames = { left: "Left", right: "Right" }; }
		store.dirty.sideNames = { ...store.dirty.sideNames, right: v };
	},
});
var roomNameField = computed({
	get: () => store.dirty?.roomName ?? "",
	set: (v) => { store.dirty.roomName = v; },
});


function makeCapField(capKey, side) {
	return computed({
		get: () => store.dirty?.[capKey]?.[side] ?? null,
		set: (v) => {
			if (!store.dirty) { return; }
			var current = store.dirty[capKey] && typeof store.dirty[capKey] === "object" ? store.dirty[capKey] : { left: null, right: null };
			var num = v === "" || v == null ? null : Number(v);
			store.dirty[capKey] = { ...current, [side]: Number.isFinite(num) ? num : null };
		},
	});
}
var leftFloor = makeCapField("tempFloor", "left");
var rightFloor = makeCapField("tempFloor", "right");
var leftCeiling = makeCapField("tempCeiling", "left");
var rightCeiling = makeCapField("tempCeiling", "right");

function reset() { configStore.methods.reset(); }
function save() { configStore.methods.save(); }
// Clears whichever of the two this device uses: the stored API token, and the session cookie that
// came from the one sign-in service. The cookie is HttpOnly, so only the server can drop it; a form
// post to /sso/logout does that and then hands over to the service, which offers to sign out of
// everything. With only a token stored there is nothing to post to, so stay in the app.
var signingOut = ref(false);
function signOut() {
	var hadToken = hasToken.value;
	api.setToken("");
	sessionStorage.removeItem("bed.authOpen");
	hasToken.value = false;
	if (hadToken) { router.push({ name: "login" }); return; }
	signingOut.value = true;
	var form = document.createElement("form");
	form.method = "post";
	form.action = "/sso/logout";
	document.body.appendChild(form);
	form.submit();
}

function hint(k) {
	switch (k) {
	case "target": return "Used when no schedule entry is active";
	case "neutral": return "Room temp at which no adjustment applies";
	case "influence": return "How much room temp shifts target (0 = ignore)";
	case "hysteresis": return "Don't change target within ±this";
	case "hold": return "Fallback override duration (used only when no schedule is set; otherwise holds until wake)";
	case "interval": return "Control loop tick frequency";
	case "preheat": return "Run at schedule start without presence for this long; stops if nobody gets in. 0 disables.";
	case "autoStop": return "Standby the dock after this long with no one in bed; resumes when occupied. 0 disables.";
	default: return "";
	}
}
</script>
